// The chip row under each automation in the Control Room's Automations card:
// "[Teams logo] Chat: Teams", "[mail glyph] Delivery: Email". The chips come
// from lib/automationChips.js. A logo is decoration: the label already names
// the tool, so the mark is hidden from screen readers and the text stays.
import React from "react";
import ToolIcon from "./ToolIcon";
import { meetingChips } from "../lib/automationChips";
import { fetchMeetingOrganizers } from "../lib/meetingCalendar";
import "./AutomationChips.css";

// A chip is a plain string or { tool, label }.
function chipParts(chip) {
  if (chip && typeof chip === "object") {
    return { tool: chip.tool || null, label: String(chip.label ?? "") };
  }
  return { tool: null, label: String(chip ?? "") };
}

export default function AutomationChips({ chips = [] }) {
  const items = (Array.isArray(chips) ? chips : [])
    .map(chipParts)
    .filter((chip) => chip.label);
  if (!items.length) return null;
  return (
    <div className="ruleMeta">
      {items.map(({ tool, label }) => (
        <span key={label} className={tool ? "chip autoChip" : "chip"}>
          {tool ? (
            <span className="autoChip__logo" aria-hidden="true">
              <ToolIcon tool={tool} />
            </span>
          ) : null}
          {label}
        </span>
      ))}
    </div>
  );
}

/**
 * Which calendars `email` has connected: { status, google, outlook }.
 * status is "loading", then "ready", or "unknown" when it cannot be read
 * (this viewer may not read that person's connections, or the call failed).
 * GET /api/calendar/connections reads stored rows only, no token refresh.
 */
export function useCalendarConnections({ apiBase, email, enabled = true }) {
  const base = String(apiBase || "").replace(/\/+$/, "");
  const target = String(email || "")
    .trim()
    .toLowerCase();
  const canRead = !!(enabled && target);
  const [state, setState] = React.useState({
    status: canRead ? "loading" : "unknown",
    google: false,
    outlook: false,
  });

  const refresh = React.useCallback(async () => {
    if (!canRead) {
      setState({ status: "unknown", google: false, outlook: false });
      return;
    }
    try {
      const r = await fetch(
        `${base}/api/calendar/connections?email=${encodeURIComponent(target)}`,
        { credentials: "include", cache: "no-store" },
      );
      if (!r.ok) throw new Error("calendar_connections_failed");
      const json = await r.json();
      setState({
        status: "ready",
        google: !!json?.google,
        outlook: !!json?.outlook,
      });
    } catch {
      // A failed re-read keeps what was already shown.
      setState((current) =>
        current.status === "ready"
          ? current
          : { status: "unknown", google: false, outlook: false },
      );
    }
  }, [base, target, canRead]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Calendars are connected in a popup (Team directory), so re-read when the
  // window gets focus back, as MeetingNotetakerCard does.
  React.useEffect(() => {
    if (!canRead) return undefined;
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [canRead, refresh]);

  return state;
}

/**
 * Who in the workspace has a calendar connected, for the meeting calendar
 * picker: { status: "loading" | "ready" | "unavailable", organizers } where
 * organizers is normalizeMeetingOrganizers() (lib/meetingCalendar.js).
 * Re-read when the window gets focus back: calendars are connected in a
 * popup from Team directory. `load` is injectable for tests.
 */
export function useMeetingOrganizers({
  apiBase,
  enabled = true,
  load = fetchMeetingOrganizers,
}) {
  const [state, setState] = React.useState({
    status: enabled ? "loading" : "unavailable",
    organizers: null,
  });
  const refresh = React.useCallback(async () => {
    if (!enabled) {
      setState({ status: "unavailable", organizers: null });
      return;
    }
    const organizers = await Promise.resolve(load({ apiBase })).catch(() => null);
    setState((current) => {
      if (organizers) return { status: "ready", organizers };
      // A failed re-read keeps what was already shown.
      return current.organizers ? current : { status: "unavailable", organizers: null };
    });
  }, [apiBase, enabled, load]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [enabled, refresh]);

  return state;
}

/**
 * The meetings automation's chips: the owner's calendar, read live.
 * `preferred` is settings.calendarWriteBackend (see meetingChips).
 */
export function OwnerCalendarChips({
  apiBase,
  ownerEmail,
  canRead = true,
  preferred = null,
}) {
  const calendars = useCalendarConnections({
    apiBase,
    email: ownerEmail,
    enabled: canRead,
  });
  return <AutomationChips chips={meetingChips(calendars, preferred)} />;
}
